import * as React from 'react';
import Papa from 'papaparse';
import { Loader } from 'Src/components/Loader/Loader';
import { superAdminGet, superAdminPost } from 'Src/helpers/service/superadmin/api';
import { getSuperAdminSchools } from 'Src/helpers/service/superadmin/schools';

// ─── Helpers ─────────────────────────────────────────────────────────────────

const EMAIL_REGEXP = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_REGEXP = /\d/;

function randomStr() {
  return Math.random()
    .toString(36)
    .substring(2, 10);
}

function formatPhone(phone: string): string | undefined {
  if (!phone || !PHONE_REGEXP.test(phone)) return undefined;
  let p = phone.replace(/[\s+]/g, '');
  if (p.startsWith('0')) p = '44' + p.slice(1);
  if (p.startsWith('440')) p = '44' + p.slice(3);
  if (!p.startsWith('44')) p = '44' + p;
  return '+' + p;
}

function getEmail(raw: string): string {
  if (raw && raw.trim() && EMAIL_REGEXP.test(raw.trim())) return raw.trim();
  return `NOTSEND_${randomStr()}@squadintouch.com`;
}

function getPhone(mobile: string, primary: string, wondeUPI: string): string {
  const m = formatPhone(mobile);
  const p = formatPhone(primary);
  return m || p || `NOTSEND_${wondeUPI || randomStr()}`;
}

function parseDOB(val: string): string {
  if (!val) return '';
  const parts = val.split(/[\/\-]/);
  if (parts.length === 3 && parts[2].length === 4) {
    const m = parts[1].length < 2 ? '0' + parts[1] : parts[1];
    const d = parts[0].length < 2 ? '0' + parts[0] : parts[0];
    return `${parts[2]}-${m}-${d}`;
  }
  return val;
}

interface ParseError {
  row: number;
  message: string;
}

interface StudentRef {
  firstName: string;
  lastName: string;
  DOB: string;
  relationship: string;
  priority: number;
  studentData?: any; // matched school student
}

interface ParentRow {
  firstName: string;
  lastName: string;
  gender?: string;
  birthday?: string;
  email: string;
  phone: string;
  misId?: string;
  students: StudentRef[];
  id?: string; // filled after POST /users
}

function parseParentsCSV(file: File): Promise<{ parents: ParentRow[]; errors: ParseError[] }> {
  return new Promise((resolve, reject) => {
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: result => {
        const errors: ParseError[] = result.errors.map(e => ({ row: e.row || 0, message: e.message }));
        const rows = result.data as any[];
        const fields = result.meta.fields || [];

        const col = (row: any, ...keys: string[]) => {
          for (const k of keys) {
            const f = fields.find(f => f.toLowerCase() === k.toLowerCase());
            if (f !== undefined && row[f] !== undefined && row[f] !== '') return row[f];
          }
          return '';
        };

        const parents: ParentRow[] = rows
          .filter(r => Object.keys(r).length > 1)
          .map((row, i) => {
            const students: StudentRef[] = [];
            for (let n = 1; n <= 4; n++) {
              const fn = col(row, `relatedToStudent_${n}_firstName`);
              const ln = col(row, `relatedToStudent_${n}_lastName`);
              const dob = col(row, `student${n}_DOB`);
              if (!fn && !ln) continue;
              students.push({
                firstName: fn,
                lastName: ln,
                DOB: dob,
                relationship: col(row, `relatedToStudent_${n}_relationship`),
                priority: Number(col(row, `relatedToStudent_${n}_priority`)) || 1
              });
            }

            const gender = col(row, 'gender').toLowerCase();

            return {
              firstName: col(row, 'firstName').trim(),
              lastName: col(row, 'lastName').trim(),
              gender:
                gender === 'male' || gender === '1'
                  ? 'MALE'
                  : gender === 'female' || gender === '0'
                  ? 'FEMALE'
                  : undefined,
              birthday: parseDOB(col(row, 'DOB')) || undefined,
              email: getEmail(col(row, 'email')),
              phone: getPhone(col(row, 'mobilePhone'), col(row, 'primaryPhone'), col(row, 'wondeUPI')),
              misId: col(row, 'misId', 'mis_id') || undefined,
              students
            };
          })
          .filter(p => p.students.length > 0);

        // Check duplicate emails/phones
        const emails = new Set<string>();
        const phones = new Set<string>();
        parents.forEach((p, i) => {
          if (!p.email.startsWith('NOTSEND_')) {
            if (emails.has(p.email)) errors.push({ row: i, message: `Duplicate email: ${p.email}` });
            else emails.add(p.email);
          }
          if (!p.phone.startsWith('NOTSEND_')) {
            if (phones.has(p.phone)) errors.push({ row: i, message: `Duplicate phone: ${p.phone}` });
            else phones.add(p.phone);
          }
        });

        resolve({ parents, errors });
      },
      error: err => reject(err)
    });
  });
}

function matchStudents(parents: ParentRow[], schoolStudents: any[]): { parents: ParentRow[]; errors: ParseError[] } {
  const errors: ParseError[] = [];

  const matched = parents.map((parent, pi) => {
    const p = { ...parent };
    p.students = parent.students.map(s => {
      const dob = parseDOB(s.DOB);
      const found = schoolStudents.filter(
        ss =>
          ss.firstName === s.firstName &&
          ss.lastName === s.lastName &&
          (dob === '' || (ss.birthday || '').startsWith(dob))
      );
      if (found.length === 0) {
        errors.push({ row: pi, message: `Student ${s.firstName} ${s.lastName} (${s.DOB}) not found in school` });
      } else if (found.length > 1) {
        errors.push({ row: pi, message: `Multiple students found for ${s.firstName} ${s.lastName}` });
      }
      return { ...s, studentData: found[0] };
    });
    return p;
  });

  return { parents: matched, errors };
}

// ─── Component ───────────────────────────────────────────────────────────────

interface Props {}
interface State {
  schoolSearch: string;
  schoolId: string;
  schoolName: string;
  schoolOptions: Array<{ id: string; name: string }>;
  isSearchingSchools: boolean;
  isLoadingSchoolData: boolean;
  schoolStudents: any[];
  parents: ParentRow[];
  parseErrors: ParseError[];
  matchErrors: ParseError[];
  isUploading: boolean;
  uploadProgress: number;
  uploadTotal: number;
  uploadErrors: string[];
  successMessage: string;
  errorMessage: string;
  importAsBlocked: boolean;
}

export class SuperAdminImportParents extends React.Component<Props, State> {
  schoolSearchTimer: any;

  constructor(props: Props) {
    super(props);
    this.state = {
      schoolSearch: '',
      schoolId: '',
      schoolName: '',
      schoolOptions: [],
      isSearchingSchools: false,
      isLoadingSchoolData: false,
      schoolStudents: [],
      parents: [],
      parseErrors: [],
      matchErrors: [],
      isUploading: false,
      uploadProgress: 0,
      uploadTotal: 0,
      uploadErrors: [],
      successMessage: '',
      errorMessage: '',
      importAsBlocked: false
    };
  }

  onSchoolSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    this.setState({
      schoolSearch: val,
      schoolId: '',
      parents: [],
      parseErrors: [],
      matchErrors: [],
      successMessage: ''
    });
    clearTimeout(this.schoolSearchTimer);
    if (val.trim().length < 2) {
      this.setState({ schoolOptions: [] });
      return;
    }
    this.schoolSearchTimer = setTimeout(() => {
      this.setState({ isSearchingSchools: true });
      getSuperAdminSchools({ where: { name: { like: val, options: 'i' } }, limit: 10, skip: 0 })
        .then(schools =>
          this.setState({
            isSearchingSchools: false,
            schoolOptions: schools.map(s => ({ id: s.id, name: s.name || '' }))
          })
        )
        .catch(() => this.setState({ isSearchingSchools: false }));
    }, 300);
  };

  onSchoolSelect = (school: { id: string; name: string }) => {
    this.setState({
      schoolId: school.id,
      schoolName: school.name,
      schoolSearch: school.name,
      schoolOptions: [],
      isLoadingSchoolData: true,
      parents: [],
      parseErrors: [],
      matchErrors: []
    });
    superAdminGet(`/schools/${school.id}/students`, { params: { filter: { limit: 5000, skip: 0 } } })
      .then((students: any[]) => this.setState({ isLoadingSchoolData: false, schoolStudents: students }))
      .catch(() => this.setState({ isLoadingSchoolData: false, errorMessage: 'Could not load school students.' }));
  };

  onFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    parseParentsCSV(file).then(({ parents, errors }) => {
      const { parents: matched, errors: matchErrors } = matchStudents(parents, this.state.schoolStudents);
      this.setState({ parents: matched, parseErrors: errors, matchErrors, successMessage: '', uploadErrors: [] });
    });
  };

  retry = (fn: () => Promise<any>, times = 5): Promise<any> => {
    let p: Promise<any> = Promise.reject(new Error('start'));
    for (let i = 0; i < times; i++) {
      p = p.catch(() => fn()).catch(() => new Promise((_, rej) => setTimeout(rej, 1000)));
    }
    return p;
  };

  onUpload = async () => {
    const { schoolId, parents, importAsBlocked } = this.state;
    this.setState({
      isUploading: true,
      uploadProgress: 0,
      uploadTotal: parents.length,
      uploadErrors: [],
      successMessage: '',
      errorMessage: ''
    });

    let progress = 0;
    const uploadErrors: string[] = [];

    for (const parent of parents) {
      try {
        // 1. Create user
        const userPayload: any = {
          firstName: parent.firstName,
          lastName: parent.lastName,
          email: parent.email,
          phone: parent.phone,
          status: importAsBlocked ? 'BLOCKED' : 'ACTIVE',
          password: `123qweQWE${randomStr()}`
        };
        if (parent.gender) userPayload.gender = parent.gender;
        if (parent.birthday) userPayload.birthday = parent.birthday;
        if (parent.misId) userPayload.misId = parent.misId;

        const createdUser: any = await this.retry(() => superAdminPost('/users', userPayload));

        // 2. Create permissions for each linked student
        for (const student of parent.students) {
          if (!student.studentData) continue;
          await this.retry(() =>
            superAdminPost(`/users/${createdUser.id}/permissions`, {
              preset: 'PARENT',
              schoolId,
              studentId: student.studentData.id,
              priority: student.priority
            })
          );
        }
      } catch (err) {
        uploadErrors.push(`${parent.firstName} ${parent.lastName} (${parent.email}): failed`);
      }
      progress++;
      this.setState({ uploadProgress: progress });
    }

    this.setState({
      isUploading: false,
      uploadErrors,
      successMessage:
        uploadErrors.length === 0
          ? `${parents.length} parent${parents.length !== 1 ? 's' : ''} imported successfully.`
          : `Import finished with ${uploadErrors.length} error${uploadErrors.length !== 1 ? 's' : ''}.`
    });
  };

  render() {
    const {
      schoolSearch,
      schoolId,
      schoolName,
      schoolOptions,
      isSearchingSchools,
      isLoadingSchoolData,
      parents,
      parseErrors,
      matchErrors,
      isUploading,
      uploadProgress,
      uploadTotal,
      uploadErrors,
      successMessage,
      errorMessage,
      importAsBlocked
    } = this.state;

    const allErrors = [...parseErrors, ...matchErrors];
    const hasMatchErrors = matchErrors.some(e => e.message.includes('not found'));
    const canUpload = parents.length > 0 && !isUploading && !hasMatchErrors;

    return (
      <div>
        <div className="mb-4">
          <h4 className="mb-1">Import parents</h4>
          <div className="text-muted small">
            Upload a CSV with columns: <code>firstName</code>, <code>lastName</code>, <code>email</code>,
            <code>mobilePhone</code>, <code>primaryPhone</code>, <code>gender</code>, <code>DOB</code>,
            <code>relatedToStudent_1_firstName</code>, <code>relatedToStudent_1_lastName</code>,
            <code>student1_DOB</code>, <code>relatedToStudent_1_relationship</code>,
            <code>relatedToStudent_1_priority</code>, etc.
          </div>
        </div>

        {/* School picker */}
        <div className="form-group" style={{ maxWidth: 420, position: 'relative' }}>
          <label className="small font-weight-bold">School</label>
          <input
            type="text"
            className="form-control form-control-sm"
            value={schoolSearch}
            onChange={this.onSchoolSearchChange}
            placeholder="Type to search school…"
          />
          {schoolId && <small className="text-success d-block mt-1">✓ {schoolName}</small>}
          {(schoolOptions.length > 0 || isSearchingSchools) && (
            <div
              style={{
                position: 'absolute',
                zIndex: 100,
                background: '#fff',
                border: '1px solid #ced4da',
                borderRadius: 4,
                width: '100%',
                maxHeight: 200,
                overflowY: 'auto'
              }}
            >
              {isSearchingSchools && <div className="p-2 text-muted small">Searching…</div>}
              {schoolOptions.map(s => (
                <div
                  key={s.id}
                  className="px-3 py-2 small"
                  style={{ cursor: 'pointer' }}
                  onMouseDown={() => this.onSchoolSelect(s)}
                >
                  {s.name}
                </div>
              ))}
            </div>
          )}
        </div>

        {isLoadingSchoolData && <Loader />}

        {schoolId && !isLoadingSchoolData && (
          <>
            <div className="form-group" style={{ maxWidth: 420 }}>
              <label className="small font-weight-bold">CSV file</label>
              <input type="file" className="form-control-file" accept=".csv" onChange={this.onFileChange} />
            </div>
            <div className="form-check mb-3">
              <input
                type="checkbox"
                className="form-check-input"
                id="importAsBlocked"
                checked={importAsBlocked}
                onChange={e => this.setState({ importAsBlocked: e.target.checked })}
              />
              <label className="form-check-label small" htmlFor="importAsBlocked">
                Import as blocked (users won't be able to login until unblocked)
              </label>
            </div>
          </>
        )}

        {allErrors.length > 0 && (
          <div className={`alert ${hasMatchErrors ? 'alert-danger' : 'alert-warning'}`}>
            <strong>
              {allErrors.length} issue{allErrors.length !== 1 ? 's' : ''} found:
            </strong>
            <ul className="mb-0 mt-1" style={{ maxHeight: 150, overflowY: 'auto' }}>
              {allErrors.map((e, i) => (
                <li key={i}>
                  Row {e.row}: {e.message}
                </li>
              ))}
            </ul>
          </div>
        )}

        {parents.length > 0 && (
          <div className="mb-3">
            <div className="text-muted small mb-2">
              Ready to import <strong>{parents.length}</strong> parent{parents.length !== 1 ? 's' : ''}.
            </div>
            <div style={{ maxHeight: 200, overflowY: 'auto', border: '1px solid #dee2e6', borderRadius: 4 }}>
              <table className="table table-sm table-bordered mb-0">
                <thead className="thead-light">
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Phone</th>
                    <th>Students</th>
                  </tr>
                </thead>
                <tbody>
                  {parents.slice(0, 20).map((p, i) => (
                    <tr key={i}>
                      <td className="small">
                        {p.firstName} {p.lastName}
                      </td>
                      <td className="small" style={{ fontSize: 11 }}>
                        {p.email}
                      </td>
                      <td className="small" style={{ fontSize: 11 }}>
                        {p.phone}
                      </td>
                      <td className="small">
                        {p.students.map((s, j) => (
                          <span key={j} className={`badge mr-1 ${s.studentData ? 'badge-success' : 'badge-danger'}`}>
                            {s.firstName} {s.lastName}
                          </span>
                        ))}
                      </td>
                    </tr>
                  ))}
                  {parents.length > 20 && (
                    <tr>
                      <td colSpan={4} className="text-muted small text-center">
                        … and {parents.length - 20} more
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {isUploading && (
          <div className="mb-3">
            <div className="small mb-1">
              Uploading {uploadProgress} / {uploadTotal}…
            </div>
            <div className="progress" style={{ height: 8 }}>
              <div
                className="progress-bar"
                style={{ width: `${uploadTotal > 0 ? (uploadProgress / uploadTotal) * 100 : 0}%` }}
              />
            </div>
          </div>
        )}

        {uploadErrors.length > 0 && (
          <div className="alert alert-danger">
            <strong>Upload errors:</strong>
            <ul className="mb-0 mt-1">
              {uploadErrors.map((e, i) => (
                <li key={i}>{e}</li>
              ))}
            </ul>
          </div>
        )}

        {errorMessage && <div className="alert alert-danger">{errorMessage}</div>}
        {successMessage && <div className="alert alert-success">{successMessage}</div>}

        {canUpload && (
          <button className="btn btn-primary btn-sm" onClick={this.onUpload}>
            Import {parents.length} parent{parents.length !== 1 ? 's' : ''}
          </button>
        )}
      </div>
    );
  }
}

